import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ArrowLeft, Bug, Lightbulb, Send } from 'lucide-react';
import {
  ladeEigeneVorschlaege,
  reicheVorschlagEin,
  type Vorschlag,
} from '@/api/anliegen.api';

// ─── Vorschlag oder Fehler melden ────────────────────────────────────────────
//
// Der Rückweg zum Betreiber. Beides auf einer Seite, weil sich beim Tippen
// oft herausstellt, dass ein „das geht nicht" eigentlich ein Wunsch ist – und
// weil zwei Formulare bedeuten würden, dass man vorher richtig wählen muss.
//
// Bei einem Fehler gehen Seite und Browserkennung mit. Nicht heimlich: es
// steht daneben, was mitgeschickt wird, und warum. „Bei mir geht es nicht"
// ohne beides ist keine Meldung, sondern ein Rätsel.

const STATUS_LABELS: Record<string, string> = {
  neu: 'Eingegangen',
  gesehen: 'Gelesen',
  geplant: 'Geplant',
  umgesetzt: 'Umgesetzt',
  abgelehnt: 'Abgelehnt',
};

const STATUS_FARBEN: Record<string, string> = {
  neu: 'bg-blue-50 text-blue-700',
  gesehen: 'bg-slate-100 text-slate-600',
  geplant: 'bg-violet-50 text-violet-700',
  umgesetzt: 'bg-green-50 text-green-700',
  abgelehnt: 'bg-slate-100 text-slate-500',
};

export function VorschlagPage() {
  const queryClient = useQueryClient();
  const location = useLocation();

  const [art, setArt] = useState<'idee' | 'fehler'>('idee');
  const [titel, setTitel] = useState('');
  const [text, setText] = useState('');

  const { data: eigene } = useQuery({
    queryKey: ['vorschlaege', 'eigene'],
    queryFn: ladeEigeneVorschlaege,
  });

  const senden = useMutation({
    mutationFn: () =>
      reicheVorschlagEin({
        art,
        titel: titel.trim(),
        text: text.trim(),
        // Die zuletzt besuchte Seite, nicht diese hier: wer einen Fehler
        // meldet, kommt von der Stelle, an der er aufgetreten ist.
        ...(art === 'fehler'
          ? {
              seite: (location.state as { von?: string } | null)?.von ?? document.referrer ?? '',
              browser: navigator.userAgent,
            }
          : {}),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['vorschlaege'] });
      setTitel('');
      setText('');
      toast.success('Danke – Ihre Nachricht ist angekommen.');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const kannSenden = titel.trim().length >= 3 && text.trim().length >= 10;

  return (
    <div className="flex h-app flex-col bg-slate-50">
      {/* Eigene Kopfzeile mit Rückweg – wie die Einstellungen. Diese Seite
          hängt an keinem Stammbaum und trägt deshalb auch nicht dessen
          Werkzeugleiste mit sich herum. */}
      <header className="flex h-14 shrink-0 items-center border-b border-slate-200 bg-white px-4 shadow-sm">
        <Link
          to="/"
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          <span className="hidden sm:inline">Zurück zum Stammbaum</span>
        </Link>
      </header>

      <main className="flex-1 overflow-y-auto">
    <div className="mx-auto max-w-2xl px-4 py-6 sm:py-8">
      <h1 className="text-xl font-semibold text-slate-900">Vorschlag oder Fehler melden</h1>
      <p className="mt-1 text-sm text-slate-500">
        Was fehlt Ihnen? Was funktioniert nicht? Beides landet beim selben Menschen.
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (kannSenden) senden.mutate();
        }}
        className="mt-6 space-y-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5"
      >
        {/* Die Wahl zuerst: sie ändert, was unten mitgeschickt wird. */}
        <div className="grid grid-cols-2 gap-2">
          {(
            [
              ['idee', 'Ein Vorschlag', Lightbulb, 'Etwas, das die Anwendung können sollte'],
              ['fehler', 'Ein Fehler', Bug, 'Etwas, das nicht funktioniert'],
            ] as const
          ).map(([wert, label, Icon, hinweis]) => (
            <button
              key={wert}
              type="button"
              onClick={() => setArt(wert)}
              className={`rounded-lg border p-3 text-left ${
                art === wert
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-slate-200 hover:bg-slate-50'
              }`}
            >
              <span className="flex items-center gap-2 text-sm font-medium text-slate-800">
                <Icon
                  className={`h-4 w-4 ${wert === 'fehler' ? 'text-red-500' : 'text-amber-500'}`}
                  aria-hidden="true"
                />
                {label}
              </span>
              <span className="mt-0.5 block text-xs text-slate-500">{hinweis}</span>
            </button>
          ))}
        </div>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">
            Worum geht es?
          </span>
          <input
            value={titel}
            onChange={(e) => setTitel(e.target.value)}
            maxLength={200}
            placeholder={art === 'fehler' ? 'Menü lässt sich nicht scrollen' : 'Ratespiel für die Familie'}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">
            {art === 'fehler' ? 'Was ist passiert?' : 'Beschreiben Sie es'}
          </span>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={5}
            maxLength={5000}
            placeholder={
              art === 'fehler'
                ? 'Was haben Sie getan, was haben Sie erwartet, was ist stattdessen passiert?'
                : 'Wofür wäre das gut? Wie stellen Sie sich das vor?'
            }
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </label>

        {/* Was mitgeht, steht da – nicht im Kleingedruckten. */}
        {art === 'fehler' && (
          <p className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-500">
            Mitgeschickt werden die zuletzt geöffnete Seite und die Kennung Ihres Browsers.
            Ohne beides ist eine Fehlermeldung schwer nachzustellen. Keine Inhalte aus Ihrem
            Stammbaum.
          </p>
        )}

        <button
          type="submit"
          disabled={!kannSenden || senden.isPending}
          className="flex w-full items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          <Send className="h-4 w-4" aria-hidden="true" />
          Absenden
        </button>
      </form>

      {eigene && eigene.length > 0 && (
        <section className="mt-8">
          <h2 className="text-sm font-semibold text-slate-700">Ihre bisherigen Meldungen</h2>
          <ul className="mt-3 space-y-3">
            {eigene.map((v) => (
              <EigenerEintrag key={v.id} vorschlag={v} />
            ))}
          </ul>
        </section>
      )}
    </div>
      </main>
    </div>
  );
}

function EigenerEintrag({ vorschlag }: { vorschlag: Vorschlag }) {
  const istFehler = vorschlag.art === 'fehler';
  const Symbol = istFehler ? Bug : Lightbulb;

  return (
    <li className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <p className="flex min-w-0 items-center gap-2 font-medium text-slate-800">
          <Symbol
            className={`h-4 w-4 shrink-0 ${istFehler ? 'text-red-500' : 'text-amber-500'}`}
            aria-hidden="true"
          />
          <span className="truncate">{vorschlag.titel}</span>
        </p>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
            STATUS_FARBEN[vorschlag.status] ?? STATUS_FARBEN.neu
          }`}
        >
          {STATUS_LABELS[vorschlag.status] ?? vorschlag.status}
        </span>
      </div>

      {/* Die Antwort ist der Grund, hier überhaupt nachzusehen. */}
      {vorschlag.antwort && (
        <p className="mt-2 border-l-2 border-blue-200 pl-3 text-sm text-slate-600">
          <span className="block text-xs font-medium text-slate-400">Antwort</span>
          {vorschlag.antwort}
        </p>
      )}
    </li>
  );
}
